import { useState } from 'react';
import type { CaptionMeta, ResolvedCaption } from '../../types/videos';

type CaptionSource = ResolvedCaption['caption_source'];

interface CaptionPanelProps {
  caption: string | null;
  captionRaw: string | null;
  captionSource: CaptionSource;
  captionMeta: CaptionMeta | null;
  onSave: (body: string) => Promise<void>;
  saving: boolean;
  error: Error | null;
}

function SourcePill({ source }: { source: CaptionSource }) {
  if (source === 'manual') {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
        manual
      </span>
    );
  }
  if (source === 'cache') {
    return (
      <span
        className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
        title="The .md sidecar is not readable right now; showing the last copy we saved."
      >
        cached
      </span>
    );
  }
  return null;
}

function MetaLine({ meta }: { meta: CaptionMeta }) {
  const parts: string[] = [];
  if (meta.author) parts.push(`@${meta.author}`);
  if (meta.posted_at) parts.push(meta.posted_at);
  if (meta.likes) parts.push(`${meta.likes} likes`);
  if (meta.comments) parts.push(`${meta.comments} comments`);
  if (parts.length === 0) return null;
  return <div className="text-xs text-gray-500">{parts.join(' · ')}</div>;
}

export function CaptionPanel({
  caption,
  captionRaw,
  captionSource,
  captionMeta,
  onSave,
  saving,
  error,
}: CaptionPanelProps) {
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(captionRaw ?? '');
  const [expanded, setExpanded] = useState(false);

  const isDirty = draft.trim() !== (captionRaw ?? '').trim();

  const handleSave = async () => {
    await onSave(draft);
    setEditMode(false);
  };

  const handleCancel = () => {
    setDraft(captionRaw ?? '');
    setEditMode(false);
  };

  const handleClear = async () => {
    await onSave('');
    setEditMode(false);
  };

  const lineCount = caption ? caption.split('\n').length : 0;
  const clamped = lineCount > 12 && !expanded;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900">Original post caption</h3>
          <SourcePill source={captionSource} />
        </div>
        {!editMode && caption !== null && (
          <button
            onClick={() => setEditMode(true)}
            className="text-xs text-gray-600 hover:text-gray-900 underline"
          >
            Edit
          </button>
        )}
      </div>

      {editMode ? (
        <div className="space-y-3">
          <textarea
            rows={10}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Original post caption"
            className="w-full text-sm text-gray-800 border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          />
          {error && <p className="text-xs text-red-600">{error.message}</p>}
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="text-sm px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="text-sm px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            {captionSource === 'manual' && (
              <button
                onClick={handleClear}
                disabled={saving}
                className="text-sm px-4 py-2 rounded text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear caption
              </button>
            )}
          </div>
        </div>
      ) : caption === null ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-500">
            No caption saved for this video. Older library entries were downloaded before captions
            were captured; paste the original post text to backfill it.
          </p>
          <button
            onClick={() => setEditMode(true)}
            className="text-sm px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800"
          >
            Add caption
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {captionMeta && <MetaLine meta={captionMeta} />}
          <div
            className={`whitespace-pre-wrap break-words text-sm text-gray-800 ${
              clamped ? 'max-h-[12em] overflow-hidden' : ''
            }`}
          >
            {caption}
          </div>
          {lineCount > 12 && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="text-xs text-gray-600 hover:text-gray-900 underline"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
